import { RefObject, useCallback, useEffect, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  createGiftOrder,
  deleteGiftOrderById,
  sendGiftVBankAlimUser,
  verifyGiftOrderById,
} from '@shared/api/giftOrder/giftOrder.controller';
import { GiftOrderCreateDto } from '@shared/api/giftOrder/giftOrder.interface';
import { User } from '@shared/api/user/user.interface';
import { CheckOutState } from '@shared/interfaces/bootpay.interface';
import { notificateError } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import {
  getGiftAgentOrder,
  stashAdminSessionForAgentOrder,
} from '@shared/utils/giftAgentOrder.util';
import { UserRole } from '@shared/types';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';

import { getGiftBootpayRequestPaymentDto } from '../utils/getGiftBootpayRequestDto';
import { scrollToInvalidField } from '../utils/scrollToInvalidField';

declare global {
  interface Window {
    Bootpay: any;
  }
}

interface UseGiftCheckoutPaymentParams {
  /** GiftOrderCreateDto를 생성하는 콜백. null 반환 시 결제 진행 안함 */
  createOrderDto: () => GiftOrderCreateDto | null;
  /** 결제 성공 후 추가 처리 (예: 장바구니 비우기) */
  onPaymentSuccess?: () => void;
  /** bugoId (리다이렉트용) */
  bugoId: string | null;
  /** 주문자 이름 */
  receiverName: string;
  /** 주문자 연락처 */
  receiverTel: string;
  /** 결제 가능 여부 (상품이 있는지) */
  hasItems: boolean;
  /** Bulk 배송 시 배송지 유효성 검증 */
  validateDeliveryAddress?: () => boolean;
  /** 주문자 이름 입력란 ref — 미입력 alert 시 해당 위치로 스크롤 */
  receiverNameRef?: RefObject<HTMLInputElement>;
  /** 주문자 연락처 입력란 ref — 미입력 alert 시 해당 위치로 스크롤 */
  receiverTelRef?: RefObject<HTMLInputElement>;
}

interface UseGiftCheckoutPaymentResult {
  /** 현재 결제 상태 */
  checkoutState: CheckOutState;
  /** 결제하기 버튼 클릭 핸들러 */
  handlePayment: () => Promise<void>;
  /** 결제 진행 중 여부 */
  isProcessing: boolean;
}

/**
 * Gift 결제 공통 로직을 처리하는 커스텀 훅
 * - Bootpay 결제 요청
 * - 결제 완료/실패 처리
 * - 가상계좌 처리
 * - Guest 사용자 처리
 */
export const useGiftCheckoutPayment = ({
  createOrderDto,
  onPaymentSuccess,
  bugoId,
  receiverName,
  receiverTel,
  hasItems,
  validateDeliveryAddress,
  receiverNameRef,
  receiverTelRef,
}: UseGiftCheckoutPaymentParams): UseGiftCheckoutPaymentResult => {
  const navigate = useNavigate();
  const notificationInstance = useRecoilValue(notificationInstanceAtom);

  // Auth
  const { storeRole, userProfile, signupOrLoginStoreGuest, ensureStoreCustomer } =
    useAuth();

  // 결제 상태
  const [checkoutState, setCheckoutState] = useState(CheckOutState.IDLE);
  const [giftOrderIdState, setGiftOrderIdState] = useState<string | undefined>();
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<boolean>(false);

  // 결제 성공 시 리다이렉트
  useEffect(() => {
    if (checkoutState === CheckOutState.DONE && !_.isNil(giftOrderIdState)) {
      onPaymentSuccess?.();
      navigate(
        `/gift-store/order/${giftOrderIdState}/payment?success=${isSuccess}&bugoId=${bugoId}`,
      );
    }
    if (checkoutState === CheckOutState.VBANK && !_.isNil(giftOrderIdState)) {
      navigate(
        `/gift-store/order/${giftOrderIdState}/payment?success=${isSuccess}&bugoId=${bugoId}`,
      );
    }
  }, [navigate, checkoutState, giftOrderIdState, isSuccess, bugoId, onPaymentSuccess]);

  // 결제 완료 처리
  const doneHandle = useCallback(
    async (data: any, giftOrderId: string) => {
      try {
        const isValid = await verifyGiftOrderById(
          giftOrderId,
          storeRole(),
          data.receipt_id,
        );

        if (isValid) {
          setIsSuccess(true);
          setGiftOrderIdState(giftOrderId);
          setCheckoutState(CheckOutState.DONE);
        } else {
          throw Error('유효하지 않은 결제 시도로 인한 취소 처리');
        }
      } catch (err: any) {
        console.error(err);
        setCheckoutState(CheckOutState.IDLE);
        notificateError(
          notificationInstance,
          `결제 실패\n${err.response?.data?.message}`,
        );
      }
    },
    [notificationInstance, storeRole],
  );

  // 가상계좌 처리 — vbank_data가 누락돼도 throw하지 않도록 방어
  const readyHandle = useCallback(async (data: any, giftOrderId: string) => {
    const { bank_account, bank_name } = data?.vbank_data ?? {};
    const vBank = bank_name && bank_account ? `${bank_name}\n${bank_account}` : '';
    if (vBank) {
      await sendGiftVBankAlimUser(giftOrderId, vBank);
      setIsSuccess(true);
    }
    // 계좌정보 유무와 무관하게 주문은 발급됐으므로 결과 페이지로 이동
    setGiftOrderIdState(giftOrderId);
    setCheckoutState(CheckOutState.VBANK);
  }, []);

  // Bootpay 결제 요청
  const onBootpayRequest = useCallback(async () => {
    if (_.isNil(userProfile)) {
      return;
    }

    const giftOrderDto = createOrderDto();
    if (!giftOrderDto) {
      return;
    }

    // 대리주문 모드 여부 — vbank 강제와 전화주문 마커에 함께 사용
    const agentOrder = bugoId ? getGiftAgentOrder(bugoId) : null;

    try {
      // 1. Backend에 GiftOrder 생성 — 대리주문이면 전화주문 마커를 실어 보낸다
      //    (구서버는 무시, 신서버는 저장해 전화주문 관리 페이지 필터에 사용)
      const { data: giftOrder } = await createGiftOrder(
        agentOrder ? { ...giftOrderDto, viaPhoneOrder: true } : giftOrderDto,
        storeRole(),
      );

      try {
        // 2. 결제 중 상태로 변경
        setCheckoutState(CheckOutState.DOING);

        // 3. Bootpay 결제 요청 — 대리주문 모드면 가상계좌만 열어
        //    관리자가 본인 카드로 잘못 결제하는 사고를 막는다
        const paymentResponse = await window.Bootpay.requestPayment(
          getGiftBootpayRequestPaymentDto({
            giftOrder,
            userProfile: userProfile as User<string>,
            method: agentOrder ? 'vbank' : undefined,
          }),
        );

        let confirmedData;
        let enable = true;

        switch (paymentResponse.event) {
          case 'issued':
            // 가상계좌 입금 완료 처리
            await readyHandle(paymentResponse.data, giftOrder._id);
            break;
          case 'done':
            // 결제 완료
            await doneHandle(paymentResponse.data, giftOrder._id);
            break;
          case 'confirm':
            enable = true;
            if (enable) {
              confirmedData = await window.Bootpay.confirm();
              if (confirmedData.event === 'done') {
                // 결제 성공
              } else if (confirmedData.event === 'error') {
                // 결제 승인 실패
              }
            } else {
              window.Bootpay.destroy();
            }
            break;
          case 'close':
            // 결제창 닫힘
            break;
        }
      } catch (error) {
        // 결제 실패 처리
        switch ((error as any).event) {
          case 'cancel':
            // 사용자가 결제창을 닫음
            console.error(error);
            setCheckoutState(CheckOutState.IDLE);
            await deleteGiftOrderById(giftOrder._id, storeRole());
            break;
          case 'error':
            // 결제 승인 중 오류
            console.error(error);
            setGiftOrderIdState(giftOrder._id);
            setCheckoutState(CheckOutState.DONE);
            break;
          default:
            // 결제창 미응답·스크립트 로드 실패 등 예기치 못한 오류 — '결제 중' 멈춤 방지
            console.error(error);
            setCheckoutState(CheckOutState.IDLE);
            notificateError(
              notificationInstance,
              '결제를 진행하지 못했습니다. 잠시 후 다시 시도해주세요.',
            );
            break;
        }
      }
    } catch (err) {
      // GiftOrder 생성 실패 등 — 사용자에게 알리고 상태 복구
      console.error(err);
      setCheckoutState(CheckOutState.IDLE);
      notificateError(
        notificationInstance,
        '주문 생성에 실패했습니다. 잠시 후 다시 시도해주세요.',
      );
    }
  }, [
    userProfile,
    createOrderDto,
    storeRole,
    readyHandle,
    doneHandle,
    notificationInstance,
    bugoId,
  ]);

  // 결제 요청 트리거
  useEffect(() => {
    if (requestState) {
      onBootpayRequest();
      setRequestState(false);
    }
  }, [requestState, onBootpayRequest]);

  // 결제하기 버튼 클릭
  const handlePayment = useCallback(async () => {
    if (!receiverName.trim()) {
      alert('주문자 이름을 입력해주세요.');
      scrollToInvalidField(receiverNameRef?.current);
      return;
    }
    if (!receiverTel.trim()) {
      alert('주문자 연락처를 입력해주세요.');
      scrollToInvalidField(receiverTelRef?.current);
      return;
    }
    if (!hasItems) {
      alert('결제할 상품이 없습니다.');
      return;
    }
    if (validateDeliveryAddress && !validateDeliveryAddress()) {
      return;
    }

    // 대리주문 모드(상주검색 → 관리자 진입): 관리자 로그인 상태라도 상주 명의
    // 게스트로 전환해 주문·가상계좌 안내·이후 상주 본인 로그인(이름+번호)이
    // 모두 상주 게스트 계정에 귀속되게 한다.
    const agentOrder = bugoId ? getGiftAgentOrder(bugoId) : null;

    try {
      if (
        _.isNil(userProfile) ||
        userProfile.roles.includes(UserRole.StoreGuest) ||
        agentOrder
      ) {
        // 게스트 전환이 관리자 token/userProfile을 덮어쓰기 전에 보관해 둔다
        // (관리자 세션이 아니면 no-op) — 배너의 "관리자 복귀"로 복원
        if (agentOrder) stashAdminSessionForAgentOrder();
        // (1) 비로그인 또는 StoreGuest → 게스트 가입/로그인
        // 이름/휴대폰번호 공백 제거 후 로그인·계정 생성
        // (예: "홍길동 " vs "홍길동"으로 별도 계정이 만들어지는 문제 방지)
        const normalizedName = receiverName.replace(/\s/g, '');
        // 전화번호는 숫자만 남김 (공백·하이픈 등 제거)
        const normalizedTel = receiverTel.replace(/[^0-9]/g, '');
        const guestSignupData = {
          name: normalizedName,
          phoneNumber: normalizedTel,
          password: normalizedTel.split('').slice(-4).join(''),
        };
        await signupOrLoginStoreGuest(guestSignupData, true);
      } else if (!userProfile.roles.includes(UserRole.StoreCustomer)) {
        // (2) 로그인했지만 StoreCustomer가 아님(Admin/StoreSeller/Bugo* 등)
        // → StoreCustomer로 additive 승격(토큰·userProfile 갱신).
        //    storeRole()이 public으로 떨어져 주문이 막히는 것을 방어한다.
        await ensureStoreCustomer(true);
      }
      // (3) 이미 StoreCustomer → 그대로 진행
      setRequestState(true);
    } catch (err) {
      console.error(err);
    }
  }, [
    receiverName,
    receiverTel,
    hasItems,
    validateDeliveryAddress,
    receiverNameRef,
    receiverTelRef,
    userProfile,
    signupOrLoginStoreGuest,
    ensureStoreCustomer,
    bugoId,
  ]);

  return {
    checkoutState,
    handlePayment,
    isProcessing: checkoutState === CheckOutState.DOING,
  };
};
